import React, { useState, useEffect, FC, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, List, ListItem, ListItemText, ListItemButton, ListItemIcon, Typography } from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
// Functions/Constants/Enum
import AMBGTooltip from 'shared/components/AMBGToolTip/AMBGToolTip';
import { colors } from 'assets/styles/AMBGTheme';
import { API_CONFIG } from 'shared/constants/api';
import * as actionTypes from 'store/action-types';
import HttpService from 'shared/services/Http.service';
import { createAction } from 'shared/util/Utility';
import { MODULE, ROUTES } from 'shared/constants/constant';
// Components
import menuItems from './component/MenuItems';
import LayoutHeader from './component/LayoutHeader';
import CompanySelect from 'features/mySales/components/CompanySelect';
// Type/Interface
import { IState } from 'shared/interface/state';
import { LayoutProps, MenuItemPropsTypes } from './Layout.types';
import { IRole } from 'features/auth/login/LoginScreen.type';
// Styles
import { Drawer, DrawerHeader, StyledIconButton, leftPanelText, leftPanelTextRoot } from './Layout.styles';
// Assets
import AMBGLogo from 'assets/images/ambg-logo.png';
import AutoruptivLogo from 'assets/images/autoruptiv-logo.png';
import AutoruptivMiniLogo from 'assets/images/autoruptiv-mini-logo.png';
import { VoteAlertIcon } from 'assets/icons/Icons';

const Layout: FC<LayoutProps> = (props: LayoutProps) => {
	const role = useSelector((state: IState) => state.auth.role as IRole);
	const isSuperAdmin = role.role_id === 1;
	const isAmbgAdmin = role.role_id === 5;
	const hasAnyPendingPolls = useSelector((state: IState) => state.common?.hasAnyPendingPolls);
	const dispatch = useDispatch();
	const [open, setOpen] = useState(true);
	const [menuListItem, setMenuListItem] = useState<MenuItemPropsTypes[]>([]);
	const [isCompanyModalOpen, setCompanyModalOpen] = useState<boolean>(false);
	const location = useLocation();
	const navigate = useNavigate();
	const hasCompanySelection = isSuperAdmin || isAmbgAdmin || role.is_sub_companies;
	const isVotingRoute = location.pathname === '/poll' || location.pathname.startsWith('/poll/');

	const fetchCommonDropdown = useCallback(async () => {
		try {
			const response = await HttpService.get(API_CONFIG.dropDownOption, {
				company_id: role.company_id
			});
			const flag = Boolean(response?.data?.hasAnyPendingPolls);
			dispatch(createAction(actionTypes.HAS_ANY_PENDING_POLLS, flag));
		} catch (error) {
			console.error(error);
			dispatch(createAction(actionTypes.HAS_ANY_PENDING_POLLS, false));
		}
	}, [dispatch, role.company_id]);

	useEffect(() => {
		const allowedModules = (role.modules || []).filter((module) => {
			if (!module?.module_name) return false;
			if (module.module_name === MODULE.VOTING) return isSuperAdmin || isAmbgAdmin;
			return true;
		});
		const filteredMenuItems = allowedModules
			.map((module) => menuItems[module.module_name])
			.filter(Boolean) as MenuItemPropsTypes[];

		const mappingItem = menuItems[MODULE.MAPPING];
		if (isSuperAdmin && mappingItem) {
			const masterIndex = filteredMenuItems.findIndex((item) => item.url === ROUTES.MASTER);
			if (masterIndex >= 0 && !filteredMenuItems.some((item) => item.url === ROUTES.MAPPING)) {
				filteredMenuItems.splice(masterIndex + 1, 0, mappingItem);
			}
		}

		setMenuListItem(filteredMenuItems);
	}, [role, isAmbgAdmin, isSuperAdmin]);

	useEffect(() => {
		if (!role?.company_id) return;
		fetchCommonDropdown();
	}, [fetchCommonDropdown, role?.company_id]);

	const handleDrawerOpen = () => {
		setOpen(true);
	};

	const handleDrawerClose = () => {
		setOpen(false);
	};

	const renderIconButton = (id: string, method: () => void, icon: any) => (
		<StyledIconButton onClick={method} id={id} data-testid={id}>
			{icon}
		</StyledIconButton>
	);
	const pathNameMatcher = (path: string) => location.pathname.startsWith(path);

	const handleListItemClick = (url: string) => {
		if (open) {
			setOpen(false);
		}
		if (hasCompanySelection && url === '/my-sales') {
			setCompanyModalOpen(true);
		} else {
			navigate(url);
		}
	};

	return (
		<>
			<Box sx={{ display: 'flex' }}>
				<Drawer variant='permanent' open={open}>
					<DrawerHeader>
						{open && (
							<Box sx={{ height: '70px' }}>
								<img
									alt='ambg logo'
									src={AMBGLogo}
									style={{ margin: '0px 20px 0 0', height: '100%' }}
								/>
							</Box>
						)}
						{open
							? renderIconButton('openDrawer', handleDrawerClose, <MenuOpenIcon />)
							: renderIconButton('closeDrawer', handleDrawerOpen, <MenuIcon />)}
					</DrawerHeader>
					<List>
						{menuListItem.map((item) => {
							const isActive = pathNameMatcher(item?.url as string);
							const activeColor = colors.primary.main;

							return (
								<AMBGTooltip title={item?.title} arrow key={item?.label} placement='right'>
									<ListItem disablePadding sx={{ display: 'block' }}>
										<ListItemButton
											id='listItemButton'
											onClick={() => handleListItemClick(item?.url as string)}
											data-testid='listItemButton'
											selected={isActive}
											sx={{
												minHeight: 48,
												justifyContent: open ? 'initial' : 'center',
												px: 2.5,
												color: isActive ? activeColor : '',
												backgroundColor: isActive ? colors.secondary.shade4 : ''
											}}
										>
											<ListItemIcon
												sx={{
													minWidth: 0,
													mr: open ? 3 : 'auto',
													justifyContent: 'center',
													color: isActive ? activeColor : '',
													...(isActive && {
														'& svg, & svg *': {
															fill: activeColor
														}
													})
												}}
											>
												{item?.icon as React.JSX.Element}
											</ListItemIcon>
											<ListItemText primary={item?.label} sx={{ opacity: open ? 1 : 0 }} />
										</ListItemButton>
									</ListItem>
								</AMBGTooltip>
							);
						})}
					</List>
					<Box sx={leftPanelTextRoot}>
						{open ? (
							<>
								<Typography sx={leftPanelText}>Developed and Maintained By</Typography>
								<img alt='autoruptiv logo' src={AutoruptivLogo} style={{ width: '80%' }} />
							</>
						) : (
							<img alt='autoruptiv logo' src={AutoruptivMiniLogo} height={35} width={35} />
						)}
					</Box>
				</Drawer>
				<Box
					component={'main'}
					width={open ? 'calc(100vw - 254px)' : 'calc(100vw - 69px)'}
					sx={{
						backgroundColor: colors.secondary.shade4,
						minHeight: '100vh',
						position: 'relative',
						transition: 'width 0.3s ease'
					}}
				>
					<LayoutHeader isDrawerOpen={open} />
					<Box sx={{ pb: hasAnyPendingPolls ? '80px' : 0 }}>{props.children}</Box>
					{!!hasAnyPendingPolls && (
						<Box
							sx={{
								position: 'fixed',
								left: open ? '254px' : '69px',
								right: 0,
								bottom: '10px',
								zIndex: 1200,
								padding: '10px 30px'
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									border: `1px solid ${colors.primary.main}`,
									backgroundColor: colors.primary.shade4,
									borderRadius: '6px',
									padding: '10px 12px'
								}}
							>
								<VoteAlertIcon height='20px' width='20px' />
								<Typography sx={{ fontSize: '18px', color: colors.primary.main }}>
									{isVotingRoute ? (
										'One vote per OEM is permitted, so internal alignment is recommended prior to voting'
									) : (
										<>
											A new poll is live!
											<Typography
												component='span'
												sx={{
													cursor: 'pointer',
													fontWeight: 600,
													textDecoration: 'underline'
												}}
												onClick={() => navigate('/poll')}
											>
												&nbsp;Click here
											</Typography>
											&nbsp;to submit your vote.
										</>
									)}
								</Typography>
							</Box>
						</Box>
					)}
				</Box>
			</Box>
			{isCompanyModalOpen && <CompanySelect onCancel={() => setCompanyModalOpen(false)} />}
		</>
	);
};

export default Layout;
